import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeClassNames from 'rehype-class-names';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Banner } from '@els/els-react--banner';
import { ModalPanel } from '@els/els-react--modal-panel';
import { extractRefsFromSummary } from '@Containers/Summary/summaryRefsUtil';
import { trackGenAIContentUpdated, trackExpandCollapseSummary, trackSurveySubmissionForSummary } from '@Containers/Summary/analytics';
import ReferencePreview from './ReferencePreview';
import ThumbsUpDown from './ThumbsUpDown';

const REF_LINK_TAG_NAME = 'cite';

const RefLinkContext = createContext({
  refsFromSummary: {},
  setOpenedReference: () => {},
});

const mdClassNamesMapping = {
  table: 'c-els-table',
  thead: 'c-els-table__head',
  tbody: 'c-els-table__body',
  tr: 'c-els-table__row',
  'thead tr': 'c-els-table__row c-els-table__row--head',
  th: 'c-els-table__cell c-els-table__cell--header',
  td: 'c-els-table__cell',
  a: 'c-els-link c-els-link--inline',
  blockquote: 'c-els-blockquote-inner c-els-blockquote--left-border c-els-blockquote--filled',
  h1: 'u-els-font-size-h4 u-els-font-family-bold',
};

export function RefLink({ children: cnr }) {
  const { refsFromSummary, setOpenedReference } = useContext(RefLinkContext);
  const index = refsFromSummary[cnr];

  return index !== undefined ? (
    <Button type="link" data-e2e="summary-ref" linkOptions={['emphasized']} onClick={() => setOpenedReference(cnr)}>
      ({index})
    </Button>
  ) : null;
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), REF_LINK_TAG_NAME],
};

const markdownComponentsMap = {
  [REF_LINK_TAG_NAME]: RefLink,
  h1: 'h3',
  h2: 'h4',
  h3: 'h5',
};

function Summary({
  summaryResponse,
  limitToReferencedDocuments,
  copySummaryToClipboard,
  toggleHowItWorksPanel,
  resetResultsScrollTop,
  activeBreadcrumb,
  params,
  metadata,
}) {
  const { summary, referencedCitations } = summaryResponse;

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    trackExpandCollapseSummary(!isExpanded);
  };

  // Map {[cnr]: index}
  const refsFromSummary = useMemo(() => extractRefsFromSummary(summary, referencedCitations), [summary, referencedCitations]);
  const [openedReference, setOpenedReference] = React.useState(null);
  const openedReferenceCitation = useMemo(
    () => openedReference && referencedCitations?.find((cit) => cit.id === openedReference),
    [openedReference, referencedCitations]
  );
  const refLinkContextValue = useMemo(() => ({ refsFromSummary, setOpenedReference }), [refsFromSummary]);

  const resetSummaryExpandedState = () => {
    setOpenedReference(null);
    setIsExpanded(false);
    if (typeof resetResultsScrollTop === 'function') {
      resetResultsScrollTop();
    }
  };

  const onCopySummaryClick = () => {
    copySummaryToClipboard(summaryResponse);
  };

  const onReferencesClick = () => {
    const cnrList = summaryResponse?.referencedCitations?.map((cit) => cit.id) || [];
    const { databaseId, sort, order } = params;
    limitToReferencedDocuments({ cnrList, databaseId, sort, order });
  };

  const onDocumentDetailsClick = (citation) => {
    const cnrList = [citation?.id];
    const { databaseId, sort, order } = params;
    limitToReferencedDocuments({ cnrList, databaseId, sort, order });
  };

  // Reset expanded state when changing to a different result set
  useEffect(
    () => () => {
      resetSummaryExpandedState();
    },
    [params.resultName, params.pageNumber]
  );

  // Reset scroll position of results when collapsing summary
  useEffect(() => {
    if (!isExpanded && typeof resetResultsScrollTop === 'function') {
      resetResultsScrollTop();
    }
  }, [isExpanded]);

  useEffect(() => {
    if (summaryResponse) {
      trackGenAIContentUpdated({ summaryResponse, activeBreadcrumb });
    }
  }, [summaryResponse]);

  return (
    <>
      {openedReference && (
        <ModalPanel
          panelPosition="right"
          modalProps={{
            onClose: () => setOpenedReference(null),
            focusTrapProps: {
              focusTrapOptions: {
                // Prevent returning focus to the reference link that opened the modal
                preventScroll: true,
              },
            },
          }}
          hideBackground
          className="c-els-modal--panel-custom">
          <ReferencePreview
            index={refsFromSummary[openedReference]}
            citation={openedReferenceCitation}
            onDocumentDetailsClick={onDocumentDetailsClick}
            params={params}
            metadata={metadata}
          />
        </ModalPanel>
      )}
      <div className="ai-search-summary__content" data-testid="summary-content">
        <RefLinkContext.Provider value={refLinkContextValue}>
          <div
            className={cn(
              's-els-non-authored-content',
              'ai-search-summary__body',
              isExpanded ? 'ai-search-summary__body--expanded' : 'ai-search-summary__body--collapsed'
            )}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], [rehypeClassNames, mdClassNamesMapping]]}
              components={markdownComponentsMap}>
              {summary}
            </ReactMarkdown>
          </div>
        </RefLinkContext.Provider>
        <div className={cn('u-els-margin-top-1x1o2 u-els-display-flex', { 'u-els-display-none': !isExpanded })}>
          <div className="ai-search-summary__buttons-splitbar u-els-padding-right-1x1o2 u-els-margin-right-1x1o2">
            <ThumbsUpDown
              thumbsUpProps={{ 'data-e2e': 'summary-like' }}
              thumbsDownProps={{ 'data-e2e': 'summary-dislike' }}
              onFeedbackChange={trackSurveySubmissionForSummary}
              ariaLabel="AI Summary feedback"
              announceLike="You liked this summary."
              announceDislike="You disliked this summary."
            />
          </div>
          <div className="ai-search-summary__buttons-splitbar u-els-padding-right-1x1o2 u-els-margin-right-1x1o2">
            <ButtonWithIcon data-e2e="copy-summary-button" type="link" sprite="DuplicateDocument" iconOnly onClick={onCopySummaryClick}>
              Copy Summary
            </ButtonWithIcon>
          </div>
          <ButtonWithIcon
            data-e2e="references-button"
            type="link"
            sprite="Link"
            iconRight
            disabled={isEmpty(referencedCitations)}
            onClick={onReferencesClick}>
            References
          </ButtonWithIcon>
        </div>
        <Banner type="info" isVisible={isExpanded} className="u-els-margin-top u-els-margin-bottom">
          <p>
            The summary is based on your original query and up to the 20 most relevant results. AI-generated content may vary in quality.
            Verify important information.
          </p>
          <ButtonWithIcon
            type="link"
            linkIconSize="xs"
            sprite="InformationOutlineCircle"
            iconRight
            className="u-els-margin-top"
            data-e2e="search-tips-button"
            onClick={toggleHowItWorksPanel}>
            Learn how it works
          </ButtonWithIcon>
        </Banner>
      </div>
      <Button type="secondary" size="small" data-e2e="summary-toggle-expanded" className="u-els-margin-top" onClick={toggleExpanded}>
        {isExpanded ? 'Collapse summary' : 'View full summary'}
      </Button>
    </>
  );
}

export default Summary;
